/**
 * Shared signal for "the cookie consent banner is currently on screen".
 *
 * Purpose: consent controls must always be clickable. Floating surfaces
 * (Consultant Assistant FAB, Home Hub Ask launcher) subscribe to this and
 * hide themselves while the banner is displayed, instead of fighting over
 * arbitrary z-index values.
 */
import { useEffect, useState } from "react";

const EVENT = "evo:consent-banner-visibility";
const ATTR = "data-consent-banner-open";

export function setConsentBannerVisible(visible: boolean): void {
  if (typeof document === "undefined") return;
  if (visible) {
    document.documentElement.setAttribute(ATTR, "1");
  } else {
    document.documentElement.removeAttribute(ATTR);
  }
  window.dispatchEvent(new CustomEvent<boolean>(EVENT, { detail: visible }));
}

export function isConsentBannerVisible(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.hasAttribute(ATTR);
}

export function useConsentBannerVisible(): boolean {
  const [visible, setVisible] = useState(isConsentBannerVisible);

  useEffect(() => {
    const handler = (e: Event) =>
      setVisible((e as CustomEvent<boolean>).detail ?? isConsentBannerVisible());
    window.addEventListener(EVENT, handler);
    // Sync in case the banner mounted before this subscriber
    setVisible(isConsentBannerVisible());
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  return visible;
}
